import { observer } from 'mobx-react-lite'
import { ReactElement, useCallback, useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSpecificConditionStore } from '../../../store/specificConditions/SpecificConditionsStore'
import { useUiStateStore } from '../../../store/uiState/UiStateStore'
import { PartsCatalogType } from '../../../store/partsCatalog/PartsCatalog'
import { StoreInstances } from '../../../store/StoreInstancesContainer'
import { MobileLaborLookupSteps } from './components/MobileLaborLookupSteps'
import { SelectionsInterface } from './interfaces'
import { SearchStoreContext } from '../../../store/search/SearchStore'
import VehicleRequiredModal from '../../../features/partsCatalog/Selections/VehicleRequiredModal.tsx'
import useSharedLaborSelectorMobileLogic from '../../../common-hooks/useSharedLaborSelectorMobileLogic.ts'
import { useLaborLookup } from '../../../store/labor/LaborStore'
import { show } from '@ebay/nice-modal-react'
import { SpecificConditionsModal } from 'src/features/specificConditions/components/SpecificConditionsModal.tsx'

interface Props extends SelectionsInterface {
  partGroupId?: string
  setHideTitle: (arg: boolean) => void
}

export const LaborLookupSelectorMobile = observer(
  (props: Props): ReactElement => {
    const { partGroupId } = props
    const { lookup: laborPartsCatalog } = useLaborLookup()
    const {
      categories,
      fetchCategories,
      fetchGroups,
      fetchPartTypes,
      resetStore,
    } = laborPartsCatalog

    const fetchData = useCallback(async () => {
      if (categories?.length === 0) {
        try {
          await fetchCategories(partGroupId)
          await fetchGroups(partGroupId)
          await fetchPartTypes()
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching labor data:', error)
        }
      }
    }, [
      categories?.length,
      fetchCategories,
      fetchGroups,
      fetchPartTypes,
      partGroupId,
    ])

    useEffect(() => {
      fetchData()
    }, [
      categories,
      fetchCategories,
      fetchData,
      fetchGroups,
      fetchPartTypes,
      partGroupId,
    ])

    useEffect(() => {
      resetStore()
    }, [resetStore])

    const { loading, setLoading, step, setStep, mode, setMode } =
      useSharedLaborSelectorMobileLogic(props)
    const navigate = useNavigate()
    const { setShowLaborResultModal } = useUiStateStore()
    const searchStore = useContext(SearchStoreContext)
    const {
      fetchSpecificConditionQuestions,
      areAllQuestionsAnswered,
      processQuestions,
    } = useSpecificConditionStore()

    const setLaborResult = setShowLaborResultModal

    const goToSearchResults = (): void => {
      searchStore.searchByNavigation()
      navigate('/searchResults')
    }

    const fetchAndShowResults = (): void => {
      setLaborResult(true)
    }

    const handleShowFitmentConditions = async () => {
      setLoading(true)
      try {
        const formattedVehicle = {
          ...StoreInstances.searchStore.currentVehicle,
          initialized: undefined,
        }

        const specificConditionQuestions =
          await fetchSpecificConditionQuestions(
            formattedVehicle,
            laborPartsCatalog.terminologies,
            PartsCatalogType.LABOR_SEARCH
          )
        if (
          specificConditionQuestions.length > 0 &&
          !areAllQuestionsAnswered()
        ) {
          show(SpecificConditionsModal, {
            proceed: goToSearchResults,
          })
        } else {
          processQuestions()
          fetchAndShowResults()
        }
      } catch (_e) {
        // eslint-disable-next-line no-console
        console.error('Error fetching labor specific conditions')
      } finally {
        setLoading(false)
      }
    }

    return (
      <>
        <MobileLaborLookupSteps
          step={step}
          setStep={setStep}
          mode={mode}
          setMode={setMode}
          loading={loading}
          onClick={handleShowFitmentConditions}
          partsCatalog={laborPartsCatalog}
        />
        <VehicleRequiredModal isMobile />
      </>
    )
  }
)
